import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import usePageTitle from '@/hooks/usePageTitle';
import { resolveType } from '@/services/ServiceContainer';
import { LeagueService } from '@/services/LeagueService';
import { LeagueResultService } from '@/services/LeagueResultService';
import { LeagueAdminService } from '@/services/LeagueAdminService';
import type { LeagueModel } from '@/models/LeagueModels';
import type { LeagueResultModel } from '@/models/LeagueResultModels';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import LoadingSpinner, { ButtonSpinner } from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import Select from 'react-select';
// import { debugTokenClaims, testAdminStatus } from '@/utils/debugToken';

function dayIdToDateString(dayId: string): string {
    // expects YYYY-MM-DD or YYYYMMDD
    if (dayId.includes('-')) return dayId;
    return dayId.replace('day_', '').replace(/_/g, '-');
}

interface PinPrizeAssignmentModal {
    open: boolean;
    prizeId: string;
    code: string;
    hole: number;
    description: string;
    amount: number;
    member: string;
    paying: boolean;
    teeName: string | null;
}

// Add TrashIcon component
const TrashIcon: React.FC<{ color: string }> = ({ color }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill={color} />
    </svg>
);

// Add EditIcon component
const EditIcon: React.FC<{ color: string }> = ({ color }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill={color} />
    </svg>
);

// Add AddIcon component
const AddIcon: React.FC<{ color: string }> = ({ color }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill={color} />
    </svg>
);

const LeagueAdmin: React.FC = () => {
    const { leagueId } = useParams<{ leagueId: string }>();
    const { theme } = useTheme();
    usePageTitle('League Admin');

    const [leagueModel, setLeagueModel] = useState<LeagueModel | null>(null);
    const [resultsByDay, setResultsByDay] = useState<Record<string, LeagueResultModel | null>>({});
    const [loading, setLoading] = useState(true);
    const [lockingDayId, setLockingDayId] = useState<string | null>(null);
    const [lockProgress, setLockProgress] = useState<{ current: number; total: number; message: string } | null>(null);
    const [calculatingDayId, setCalculatingDayId] = useState<string | null>(null);
    const [activatingDayId, setActivatingDayId] = useState<string | null>(null);
    const [showCompleteConfirmModal, setShowCompleteConfirmModal] = useState(false);
    const [showLockConfirmModal, setShowLockConfirmModal] = useState(false);
    const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
    const [dayToLock, setDayToLock] = useState<string | null>(null);
    const [dayToCancel, setDayToCancel] = useState<string | null>(null);
    const [weekToComplete, setWeekToComplete] = useState<string | null>(null);
    const [customPayoutModal, setCustomPayoutModal] = useState<{
        open: boolean;
        scheduleDayId: string | null;
        member: string;
        amount: string;
        description: string;
        paying: boolean;
    }>({
        open: false,
        scheduleDayId: null,
        member: '',
        amount: '',
        description: '',
        paying: false,
    });
    const [removePayoutModal, setRemovePayoutModal] = useState<{
        open: boolean;
        scheduleDayId: string | null;
        memberEmail: string;
        payoutId: string;
        memberName: string;
        amount: number | null;
        description: string | null;
    }>({
        open: false,
        scheduleDayId: null,
        memberEmail: '',
        payoutId: '',
        memberName: '',
        amount: null,
        description: null,
    });
    const [pinPrizeAssignmentModal, setPinPrizeAssignmentModal] = useState<PinPrizeAssignmentModal>({
        open: false,
        prizeId: '',
        code: '',
        hole: 0,
        description: '',
        amount: 0,
        member: '',
        paying: false,
        teeName: null,
    });
    const [unassignPinPrizeModal, setUnassignPinPrizeModal] = useState<{
        open: boolean;
        scheduleDayId: string | null;
        prizeId: string;
        memberName: string;
        amount: number;
        description: string;
        hole: number;
        teeName: string | null;
        paying: boolean;
    }>({
        open: false,
        scheduleDayId: null,
        prizeId: '',
        memberName: '',
        amount: 0,
        description: '',
        hole: 0,
        teeName: null,
        paying: false,
    });
    const [errorModal, setErrorModal] = useState<{
        open: boolean;
        message: string;
    }>({
        open: false,
        message: '',
    });
    const [selectedDayId, setSelectedDayId] = useState<string | null>(null);

    const inputStyle = {
        width: '100%',
        padding: theme.spacing.SMALL,
        marginBottom: theme.spacing.MEDIUM,
        border: `1px solid ${theme.colors.BORDER}`,
        borderRadius: theme.borderRadius.SMALL,
        fontSize: theme.fonts.MEDIUM,
        backgroundColor: theme.colors.BACKGROUND,
        color: theme.colors.TEXT,
    };

    const buttonStyle = {
        padding: '10px 20px',
        border: 'none',
        borderRadius: theme.borderRadius.SMALL,
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '1em',
    };

    const fetchData = async (dayId?: string | null) => {
        if (!leagueId) return;
        setLoading(true);
        try {
            const leagueService = resolveType(LeagueService);
            const leagueData = await leagueService.get(leagueId);
            setLeagueModel(leagueData);
            if (!leagueData) {
                setErrorModal({
                    open: true,
                    message: 'League not found',
                });
                setLoading(false);
                return;
            }
            const leagueResultService = resolveType(LeagueResultService);
            if (dayId) {
                // Only fetch result for the given dayId
                const resultId = `${leagueId}_${dayId}`;
                const result = await leagueResultService.get(resultId);
                setResultsByDay(prev => ({ ...prev, [dayId]: result }));
            } else {
                // Fetch all leagueResults for each schedule item
                const results: Record<string, LeagueResultModel | null> = {};
                for (const schedule of leagueData.schedule) {
                    const resultId = `${leagueId}_${schedule.dayId}`;
                    results[schedule.dayId] = await leagueResultService.get(resultId);
                }
                setResultsByDay(results);
            }
        } catch (err) {
            setErrorModal({
                open: true,
                message: 'Failed to load league or results',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [leagueId]);

    // Determine initialSlide index
    let initialSlide = 0;
    if (leagueModel && leagueModel.schedule.length > 0) {
        const activeIdx = leagueModel.schedule.findIndex(s => s.status === 'active');
        const lockedIdx = leagueModel.schedule.findIndex(s => s.status === 'locked');
        if (activeIdx !== -1) {
            initialSlide = activeIdx;
        } else if (lockedIdx !== -1) {
            initialSlide = lockedIdx;
        } else {
            // Closest to today
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            let minDiff = Infinity;
            leagueModel.schedule.forEach((s, i) => {
                const [year, month, day] = dayIdToDateString(s.dayId).split('-');
                const d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                const diff = Math.abs(d.getTime() - today.getTime());
                if (diff < minDiff) {
                    minDiff = diff;
                    initialSlide = i;
                }
            });
        }
    }

    const settings = {
        centerMode: true,
        centerPadding: '40px',
        dots: true,
        infinite: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        swipeToSlide: true,
        arrows: false,
        initialSlide,
        appendDots: (dots: React.ReactNode) => (
            <div style={{
                width: '100%',
                margin: 0,
                padding: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                position: 'static',
            }}>
                <ul style={{
                    width: '100%',
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 8,
                    margin: 0,
                    padding: 0,
                    listStyle: 'none',
                }}>{dots}</ul>
            </div>
        ),
        responsive: [
            {
                breakpoint: 600,
                settings: {
                    centerPadding: '16px',
                }
            }
        ]
    };

    const formatDate = (dayId: string) => {
        const [year, month, day] = dayIdToDateString(dayId).split('-');
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric'
        });
    };

    const getMemberName = (encodedEmail: string): string => {
        if (!leagueModel || !leagueModel.members) return encodedEmail;
        const member = leagueModel.members[encodedEmail];
        return member ? `${member.firstName} ${member.lastName}` : encodedEmail;
    };

    // const leagueMembers = leagueModel?.members || {};
    // const memberOptions: MemberOption[] = Object.values(leagueMembers).map((m: LeagueMemberModel) => {
    //     const leagueMember = leagueMembers[m.encodedEmail];
    //     const label = leagueMember
    //         ? `${leagueMember.firstName || ''} ${leagueMember.lastName || ''} (${m.encodedEmail})`
    //         : m.encodedEmail;
    //     return { value: m.encodedEmail, label };
    // }).sort((a, b) => a.label.localeCompare(b.label));

    const handleAssignPinPrize = async () => {
        if (!leagueId || !selectedDayId || !pinPrizeAssignmentModal.member) return;
        setPinPrizeAssignmentModal(prev => ({ ...prev, paying: true }));
        try {
            const leagueAdminService = resolveType(LeagueAdminService);
            await leagueAdminService.assignPinPrize(
                leagueId,
                selectedDayId,
                pinPrizeAssignmentModal.prizeId,
                pinPrizeAssignmentModal.code,
                pinPrizeAssignmentModal.member,
                pinPrizeAssignmentModal.teeName || '',
                pinPrizeAssignmentModal.hole,
                pinPrizeAssignmentModal.amount,
                pinPrizeAssignmentModal.description
            );
            setPinPrizeAssignmentModal(prev => ({ ...prev, open: false }));
            await fetchData(selectedDayId);
        } catch (error) {
            console.error('Error assigning pin prize:', error);
            setErrorModal({
                open: true,
                message: error instanceof Error ? error.message : 'Failed to assign pin prize',
            });
        } finally {
            setPinPrizeAssignmentModal(prev => ({ ...prev, paying: false }));
        }
    };

    const handleCompleteWeekClick = (dayId: string) => {
        setWeekToComplete(dayId);
        setShowCompleteConfirmModal(true);
    };

    const handleConfirmCompleteWeek = async () => {
        if (!weekToComplete) return;

        setCalculatingDayId(weekToComplete);
        try {
            const leagueAdminService = resolveType(LeagueAdminService);
            await leagueAdminService.completeDay(leagueId!, weekToComplete);
            await fetchData(weekToComplete); // Refresh data for the specific day
        } catch (err) {
            setErrorModal({
                open: true,
                message: (err as Error).message || 'Failed to complete week',
            });
        } finally {
            setCalculatingDayId(null);
            setShowCompleteConfirmModal(false);
            setWeekToComplete(null);
        }
    };

    const handleLockDayClick = (dayId: string) => {
        setDayToLock(dayId);
        setShowLockConfirmModal(true);
    };

    const handleConfirmLockDay = async () => {
        if (!dayToLock) return;

        setLockingDayId(dayToLock);
        setLockProgress(null);
        setShowLockConfirmModal(false);
        try {
            const leagueAdminService = resolveType(LeagueAdminService) as LeagueAdminService;
            await leagueAdminService.lockDay(
                leagueId || '',
                dayToLock,
                (current, total, message) => setLockProgress({ current, total, message })
            );
            await fetchData(dayToLock);
        } catch (err: any) {
            alert('Failed to lock day: ' + (err && (err.message || err.toString())));
        } finally {
            setLockingDayId(null);
            setLockProgress(null);
            setDayToLock(null);
        }
    };

    // const handleCancelDayClick = (dayId: string) => {
    //     setDayToCancel(dayId);
    //     setShowCancelConfirmModal(true);
    // };

    const handleConfirmCancelDay = async () => {
        if (!dayToCancel) return;

        setShowCancelConfirmModal(false);
        try {
            const leagueAdminService = resolveType(LeagueAdminService) as LeagueAdminService;
            await leagueAdminService.cancelDay(leagueId || '', dayToCancel);
            await fetchData(dayToCancel);
        } catch (err: any) {
            alert('Failed to cancel day: ' + (err && (err.message || err.toString())));
        } finally {
            setDayToCancel(null);
        }
    };

    return (
        <div style={{ padding: theme.spacing.LARGE }}>
            <h1 style={{ color: theme.colors.TEXT, marginBottom: theme.spacing.LARGE }}>
                League Admin
            </h1>
            <div style={{ color: theme.colors.TEXT_SECONDARY, marginBottom: theme.spacing.LARGE }}>
                <b>{leagueModel?.name}</b>
                {/* Debug Token button temporarily hidden - keeping functionality in place
                <button
                    style={{
                        marginLeft: theme.spacing.MEDIUM,
                        background: theme.colors.WARNING,
                        color: theme.colors.WHITE,
                        fontWeight: 600,
                        fontSize: '0.8em',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        outline: 'none',
                    }}
                    onClick={async () => {
                        console.log('🔍 Debugging token for league:', leagueId);
                        await debugTokenClaims();
                        if (leagueId) {
                            await testAdminStatus(leagueId);
                        }
                    }}
                >
                    Debug Token
                </button>
                */}
            </div>
            {loading ? (
                <LoadingSpinner />
            ) : leagueModel && leagueModel.schedule.length > 0 ? (
                <div style={{
                    width: '100vw',
                    position: 'relative',
                    left: '50%',
                    right: '50%',
                    marginLeft: '-50vw',
                    marginRight: '-50vw',
                    boxSizing: 'border-box',
                    overflowX: 'hidden',
                }}>
                    <div style={{
                        marginTop: 0,
                        width: '100%',
                        margin: 0,
                        boxSizing: 'border-box',
                        overflow: 'visible',
                        position: 'relative',
                        padding: 0,
                    }}>
                        <style>{`
                          .league-admin-carousel .slick-list { overflow: visible !important; }
                          .league-admin-carousel .slick-dots li button:before {
                            font-size: 18px !important;
                            color: #888;
                          }
                          .league-admin-carousel .slick-dots li.slick-active button:before {
                            color: #222;
                          }
                        `}</style>
                        <Slider {...settings} className="league-admin-carousel">
                            {leagueModel.schedule.map((schedule, idx) => {
                                const result = resultsByDay[schedule.dayId];
                                // Determine background color and status icon
                                let bgColor = theme.colors.SURFACE;
                                let statusIcon = '';
                                if (schedule.status === 'active' || schedule.status === 'locked') {
                                    bgColor = theme.colors.HIGHLIGHTED_ITEM;
                                    statusIcon = schedule.status === 'active' ? '🟢' : '🔒';
                                } else if (schedule.status === 'completed') {
                                    bgColor = theme.colors.STATUS_COMPLETED; // very light green
                                    statusIcon = '✅';
                                } else if (schedule.status === 'cancelled') {
                                    bgColor = theme.colors.STATUS_CANCELLED; // light red
                                    statusIcon = '❌';
                                } else if (schedule.status === 'notstarted') {
                                    statusIcon = '⏳';
                                }
                                const roundsCompleted = result && result.members ? Object.keys(result.members).filter(k => result.members[k].holesCompleted == 9).length : 0;
                                const totalMembers = leagueModel.members ? Object.keys(leagueModel.members).length : 0;
                                let textColor = theme.colors.TEXT;
                                let secondaryTextColor = theme.colors.TEXT_SECONDARY;
                                if (schedule.status === 'cancelled' || schedule.status === 'completed') {
                                    textColor = theme.colors.BLACK;
                                    secondaryTextColor = theme.colors.TEXT;
                                }
                                return (
                                    <div key={schedule.dayId} data-index={idx}>
                                        <div style={{
                                            background: bgColor,
                                            border: `1px solid ${theme.colors.BORDER}`,
                                            borderRadius: theme.borderRadius.MEDIUM,
                                            padding: theme.spacing.LARGE,
                                            minHeight: 180,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: theme.spacing.MEDIUM,
                                            marginBottom: 32
                                        }}>
                                            <h2 style={{ color: textColor, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.SMALL }}>
                                                    {statusIcon && <span style={{ fontSize: '1.2em' }}>{statusIcon}</span>}
                                                    {formatDate(schedule.dayId)}
                                                </span>
                                                <span style={{ fontSize: '0.8em', color: secondaryTextColor }}>
                                                    ({schedule.holesToPlay === 'front9' ? 'Front 9' : schedule.holesToPlay === 'back9' ? 'Back 9' : 'All 18'})
                                                </span>
                                            </h2>
                                            <div style={{ color: textColor }}>
                                                <b>Rounds Submitted:</b> {roundsCompleted} of {totalMembers} ({(roundsCompleted / totalMembers * 100).toFixed(1)}%)
                                            </div>
                                            {/* Status Action Button */}
                                            {schedule.status === 'active' && (
                                                <button
                                                    style={{
                                                        marginTop: theme.spacing.MEDIUM,
                                                        background: theme.colors.PRIMARY,
                                                        color: theme.colors.WHITE,
                                                        fontWeight: 600,
                                                        fontSize: '1em',
                                                        padding: '12px 24px',
                                                        borderRadius: '8px',
                                                        border: 'none',
                                                        cursor: lockingDayId === schedule.dayId ? 'default' : 'pointer',
                                                        outline: 'none',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                        opacity: lockingDayId === schedule.dayId ? 0.7 : 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                    onClick={() => handleLockDayClick(schedule.dayId)}
                                                >
                                                    {lockingDayId === schedule.dayId ? (
                                                        lockProgress ? (
                                                            <span style={{ fontSize: '0.9em' }}>
                                                                {lockProgress.current == -1 ? (
                                                                    <>
                                                                        <ButtonSpinner size={20} color={theme.colors.WHITE} /> {lockProgress.message}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <ButtonSpinner size={20} color={theme.colors.WHITE} /> Processing {lockProgress.current} of {lockProgress.total}...
                                                                    </>
                                                                )}
                                                            </span>
                                                        ) : (
                                                            <ButtonSpinner size={20} color={theme.colors.WHITE} />
                                                        )
                                                    ) : 'Lock'}
                                                </button>
                                            )}
                                            {schedule.status === 'locked' && (
                                                <>
                                                </>
                                            )}
                                            {schedule.status === 'notstarted' &&
                                                !(leagueModel.schedule.some(s => (s.status === 'active' || s.status === 'locked'))) && (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.SMALL }}>
                                                        <button
                                                            style={{
                                                                marginTop: theme.spacing.MEDIUM,
                                                                background: theme.colors.SUCCESS,
                                                                color: theme.colors.WHITE,
                                                                fontWeight: 600,
                                                                fontSize: '1em',
                                                                padding: '12px 24px',
                                                                borderRadius: '8px',
                                                                border: 'none',
                                                                cursor: activatingDayId === schedule.dayId ? 'default' : 'pointer',
                                                                outline: 'none',
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                                opacity: activatingDayId === schedule.dayId ? 0.7 : 1,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                            }}
                                                            onClick={async () => {
                                                                setActivatingDayId(schedule.dayId);
                                                                try {
                                                                    const leagueAdminService = resolveType(LeagueAdminService);
                                                                    await leagueAdminService.activateDay(leagueId || '', schedule.dayId);
                                                                    await fetchData(schedule.dayId);
                                                                } catch (err: any) {
                                                                    alert('Failed to activate day: ' + (err && (err.message || err.toString())));
                                                                } finally {
                                                                    setActivatingDayId(null);
                                                                }
                                                            }}
                                                        >
                                                            {activatingDayId === schedule.dayId ? (
                                                                <ButtonSpinner size={20} color={theme.colors.WHITE} />
                                                            ) : 'Activate'}
                                                        </button>
                                                        {/* Cancel button temporarily hidden - keeping logic in place
                                                        <button
                                                            style={{
                                                                background: theme.colors.ERROR,
                                                                color: theme.colors.WHITE,
                                                                fontWeight: 600,
                                                                fontSize: '1em',
                                                                padding: '12px 24px',
                                                                borderRadius: '8px',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                                outline: 'none',
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                            }}
                                                            onClick={() => handleCancelDayClick(schedule.dayId)}
                                                        >
                                                            Cancel
                                                        </button>
                                                        */}
                                                    </div>
                                                )}
                                            {result && leagueModel && leagueModel.flights && result.members && Object.keys(result.members).length > 0 && (
                                                <>
                                                    {/* Pin Prizes Section */}
                                                    <div style={{ marginTop: theme.spacing.MEDIUM }}>
                                                        <div style={{ fontWeight: 600, marginBottom: theme.spacing.SMALL, color: (schedule.status === 'cancelled' || schedule.status === 'completed') ? theme.colors.BLACK : theme.colors.TEXT }}>
                                                            Pin Prizes
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                            {schedule.pinPrizes && schedule.pinPrizes.length > 0 ? (
                                                                schedule.pinPrizes.map((prize, index) => (
                                                                    <div
                                                                        key={index}
                                                                        style={{
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            justifyContent: 'space-between',
                                                                            alignItems: 'stretch',
                                                                            padding: theme.spacing.SMALL,
                                                                            backgroundColor: theme.colors.SURFACE,
                                                                            borderRadius: theme.borderRadius.SMALL,
                                                                            border: `1px solid ${theme.colors.BORDER}`,
                                                                            marginBottom: theme.spacing.SMALL
                                                                        }}
                                                                    >
                                                                        {/* First line: Hole and Description */}
                                                                        <div style={{ fontWeight: 500, color: theme.colors.TEXT, marginBottom: 4 }}>
                                                                            Hole {prize.hole} : {prize.description}
                                                                        </div>
                                                                        {/* Second line: Tee, Amount, Button */}
                                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing.MEDIUM }}>
                                                                            <div style={{ color: theme.colors.TEXT_SECONDARY, fontSize: '0.95em' }}>
                                                                                Tee : {prize.teeName || '-'}
                                                                            </div>
                                                                            <div style={{ fontWeight: 600, color: theme.colors.TEXT, minWidth: 60, textAlign: 'right' }}>
                                                                                ${prize.amount.toFixed(2)}
                                                                            </div>
                                                                        </div>
                                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing.MEDIUM }}>
                                                                            {result && result.members && Object.entries(result.members).find(([_, member]) =>
                                                                                member.payouts?.some(p => p.id === prize.id)
                                                                            ) ? (
                                                                                <>
                                                                                    <div style={{ color: theme.colors.TEXT, fontWeight: 500, fontSize: '0.9em', marginTop: 4 }}>
                                                                                        {(() => {
                                                                                            const entry = result && result.members ? Object.entries(result.members).find(([_, member]) =>
                                                                                                member.payouts?.some(p => p.id === prize.id)
                                                                                            ) : null;
                                                                                            if (!entry || !leagueModel) return '';
                                                                                            const [memberEmail] = entry;
                                                                                            const member = leagueModel.members[memberEmail];
                                                                                            return member ? `${member.firstName} ${member.lastName}` : '';
                                                                                        })()}
                                                                                    </div>
                                                                                    {(schedule.status === 'active' || schedule.status === 'locked') && (
                                                                                        <button
                                                                                            onClick={() => {
                                                                                                const entry = result && result.members ? Object.entries(result.members).find(([_, member]) =>
                                                                                                    member.payouts?.some(p => p.id === prize.id)
                                                                                                ) : null;
                                                                                                if (!entry || !leagueModel) return;
                                                                                                const [memberEmail] = entry;
                                                                                                const member = leagueModel.members[memberEmail];
                                                                                                if (member) {
                                                                                                    setUnassignPinPrizeModal({
                                                                                                        open: true,
                                                                                                        scheduleDayId: result.dayId,
                                                                                                        prizeId: prize.id,
                                                                                                        memberName: `${member.firstName} ${member.lastName}`,
                                                                                                        amount: prize.amount,
                                                                                                        description: prize.description,
                                                                                                        hole: prize.hole,
                                                                                                        teeName: prize.teeName,
                                                                                                        paying: false,
                                                                                                    });
                                                                                                }
                                                                                            }}
                                                                                            title="Unassign"
                                                                                            style={{
                                                                                                background: 'none',
                                                                                                border: 'none',
                                                                                                color: theme.colors.ERROR,
                                                                                                cursor: 'pointer',
                                                                                                padding: 0,
                                                                                                display: 'flex',
                                                                                                alignItems: 'center',
                                                                                                justifyContent: 'center',
                                                                                                width: 24,
                                                                                                height: 24,
                                                                                            }}
                                                                                        >
                                                                                            <TrashIcon color={theme.colors.ERROR} />
                                                                                        </button>
                                                                                    )}
                                                                                </>
                                                                            ) : (
                                                                                (schedule.status === 'active' || schedule.status === 'locked') && (
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            setSelectedDayId(schedule.dayId);
                                                                                            setPinPrizeAssignmentModal({
                                                                                                open: true,
                                                                                                prizeId: prize.id,
                                                                                                code: prize.code,
                                                                                                hole: prize.hole,
                                                                                                description: prize.description,
                                                                                                amount: prize.amount,
                                                                                                member: '',
                                                                                                paying: false,
                                                                                                teeName: prize.teeName,
                                                                                            });
                                                                                        }}
                                                                                        title="Assign"
                                                                                        style={{
                                                                                            background: theme.colors.PRIMARY,
                                                                                            border: 'none',
                                                                                            color: theme.colors.WHITE,
                                                                                            cursor: 'pointer',
                                                                                            padding: 0,
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            justifyContent: 'center',
                                                                                            width: 24,
                                                                                            height: 24,
                                                                                            borderRadius: theme.borderRadius.SMALL,
                                                                                        }}
                                                                                    >
                                                                                        <EditIcon color={theme.colors.WHITE} />
                                                                                    </button>
                                                                                )
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div style={{ color: (schedule.status === 'cancelled' || schedule.status === 'completed') ? theme.colors.BLACK : theme.colors.TEXT_SECONDARY, fontStyle: 'italic', padding: theme.spacing.SMALL }}>
                                                                    (None)
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Custom Payouts Section */}
                                                    <div style={{ marginTop: theme.spacing.LARGE }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            flexDirection: 'row',
                                                            alignItems: 'center',
                                                            gap: theme.spacing.SMALL
                                                        }}>
                                                            <div style={{ fontWeight: 600, color: (schedule.status === 'cancelled' || schedule.status === 'completed') ? theme.colors.BLACK : theme.colors.TEXT }}>
                                                                Custom Payouts
                                                            </div>
                                                            {schedule.status !== 'completed' && (
                                                                <button
                                                                    style={{
                                                                        background: theme.colors.ACCENT,
                                                                        color: theme.colors.WHITE,
                                                                        padding: '4px',
                                                                        borderRadius: theme.borderRadius.SMALL,
                                                                        border: 'none',
                                                                        cursor: 'pointer',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        width: '24px',
                                                                        height: '24px'
                                                                    }}
                                                                    onClick={() =>
                                                                        setCustomPayoutModal({
                                                                            open: true,
                                                                            scheduleDayId: schedule.dayId,
                                                                            member: '',
                                                                            amount: '',
                                                                            description: '',
                                                                            paying: false,
                                                                        })
                                                                    }
                                                                    title="Add Custom Payout"
                                                                >
                                                                    <AddIcon color={theme.colors.WHITE} />
                                                                </button>
                                                            )}
                                                        </div>
                                                        {result && result.members && Object.entries(result.members).map(([encodedEmail, member]) => {
                                                            const customPayouts = (member.payouts || []).filter(p => p.code === 'CUSTOM');
                                                            if (customPayouts.length === 0) return null;

                                                            const memberInfo = leagueModel.members[encodedEmail];
                                                            const memberName = memberInfo ? `${memberInfo.firstName || ''} ${memberInfo.lastName || ''}` : encodedEmail;

                                                            return (
                                                                <div key={encodedEmail} style={{
                                                                    border: `1px solid ${theme.colors.BORDER}`,
                                                                    borderRadius: theme.borderRadius.SMALL,
                                                                    padding: theme.spacing.MEDIUM,
                                                                    backgroundColor: theme.colors.SURFACE
                                                                }}>
                                                                    <div style={{
                                                                        fontWeight: 600,
                                                                        color: theme.colors.TEXT,
                                                                        marginBottom: theme.spacing.SMALL
                                                                    }}>
                                                                        {memberName}
                                                                    </div>
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                                        {customPayouts.map(payout => (
                                                                            <div key={payout.id} style={{
                                                                                display: 'flex',
                                                                                justifyContent: 'space-between',
                                                                                alignItems: 'center',
                                                                                padding: theme.spacing.SMALL,
                                                                                backgroundColor: theme.colors.BACKGROUND,
                                                                                borderRadius: theme.borderRadius.SMALL
                                                                            }}>
                                                                                <div style={{ color: theme.colors.TEXT_SECONDARY }}>
                                                                                    <div style={{ fontWeight: 500, color: theme.colors.TEXT }}>${payout.amount?.toFixed(2)}</div>
                                                                                    <div>{payout.description}</div>
                                                                                </div>
                                                                                {schedule.status === 'locked' && (
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            setRemovePayoutModal({
                                                                                                open: true,
                                                                                                scheduleDayId: result.dayId,
                                                                                                memberEmail: encodedEmail,
                                                                                                payoutId: payout.id,
                                                                                                memberName: memberName,
                                                                                                amount: payout.amount,
                                                                                                description: payout.description
                                                                                            });
                                                                                        }}
                                                                                        style={{
                                                                                            background: theme.colors.ERROR,
                                                                                            color: theme.colors.WHITE,
                                                                                            padding: '4px 8px',
                                                                                            borderRadius: theme.borderRadius.SMALL,
                                                                                            border: 'none',
                                                                                            cursor: 'pointer',
                                                                                            fontSize: theme.fonts.SMALL
                                                                                        }}
                                                                                    >
                                                                                        Remove
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                        {result && result.members && Object.values(result.members).every(member =>
                                                            !member.payouts || !member.payouts.some(p => p.code === 'CUSTOM')
                                                        ) && (
                                                                <div style={{
                                                                    color: (schedule.status === 'cancelled' || schedule.status === 'completed') ? theme.colors.BLACK : theme.colors.TEXT_SECONDARY,
                                                                    fontStyle: 'italic',
                                                                    padding: theme.spacing.SMALL
                                                                }}>
                                                                    (None)
                                                                </div>
                                                            )}
                                                    </div>


                                                    {/* Total Payouts by Flight */}
                                                    <div style={{ marginTop: theme.spacing.LARGE }}>
                                                        <div style={{ fontWeight: 600, color: theme.colors.TEXT, marginBottom: theme.spacing.SMALL }}>
                                                            Total Payouts by Flight
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                            {leagueModel.flights.map(flight => {
                                                                // Sum totalPayout for members in this flight
                                                                const total = result && result.members ? Object.values(result.members)
                                                                    .filter(m => m.flightId === flight.id && typeof m.totalLeaguePayout === 'number')
                                                                    .reduce((sum, m) => sum + (m.totalLeaguePayout || 0), 0) : 0;
                                                                return (
                                                                    <div key={flight.id} style={{
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                        padding: theme.spacing.SMALL,
                                                                        backgroundColor: theme.colors.SURFACE,
                                                                        borderRadius: theme.borderRadius.SMALL,
                                                                        border: `1px solid ${theme.colors.BORDER}`
                                                                    }}>
                                                                        <div style={{ fontWeight: 500, color: theme.colors.TEXT }}>{flight.name}</div>
                                                                        <div style={{ fontWeight: 600, color: theme.colors.TEXT }}>${total.toFixed(2)}</div>
                                                                    </div>
                                                                );
                                                            })}
                                                            <div style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                paddingTop: theme.spacing.MEDIUM,
                                                                marginTop: theme.spacing.MEDIUM,
                                                                borderTop: `2px solid ${theme.colors.PRIMARY}`
                                                            }}>
                                                                <div style={{ fontWeight: 700, color: theme.colors.PRIMARY, fontSize: '1.1em' }}>League Total</div>
                                                                <div style={{ fontWeight: 700, color: theme.colors.PRIMARY, fontSize: '1.1em' }}>
                                                                    ${result && result.members ? Object.values(result.members).reduce((total, member) => total + (member.totalPayout || 0), 0).toFixed(2) : '0.00'}
                                                                </div>
                                                            </div>
                                                            {schedule.status === 'locked' && (
                                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: theme.spacing.MEDIUM, padding: theme.spacing.SMALL }}>
                                                                    {/* <button
                                                                        onClick={async () => {
                                                                            setCalculatingDayId(schedule.dayId);
                                                                            try {
                                                                                const leagueAdminService = resolveType(LeagueAdminService) as LeagueAdminService;
                                                                                await leagueAdminService.calculateDay(leagueId || '', schedule.dayId || '');
                                                                                await fetchData(schedule.dayId);
                                                                            } catch (err: any) {
                                                                                alert('Failed to calculate day: ' + (err && (err.message || err.toString())));
                                                                            } finally {
                                                                                setCalculatingDayId(null);
                                                                            }
                                                                        }}
                                                                        disabled={calculatingDayId === schedule.dayId}
                                                                        style={{ ...buttonStyle, backgroundColor: theme.colors.ACCENT, color: theme.colors.WHITE }}
                                                                    >
                                                                        {calculatingDayId === schedule.dayId ? <ButtonSpinner size={20} color={theme.colors.WHITE} /> : 'Re-calculate'}
                                                                    </button> */}
                                                                    <button
                                                                        onClick={() => handleCompleteWeekClick(schedule.dayId)}
                                                                        disabled={calculatingDayId === schedule.dayId}
                                                                        style={{ ...buttonStyle, backgroundColor: theme.colors.SUCCESS, color: theme.colors.WHITE }}
                                                                    >
                                                                        {calculatingDayId === schedule.dayId ? <ButtonSpinner size={20} color={theme.colors.WHITE} /> : 'Complete'}
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Gross Skins Section */}
                                                    <div style={{ marginTop: theme.spacing.LARGE }}>
                                                        <div style={{ fontWeight: 600, color: theme.colors.TEXT, marginBottom: theme.spacing.SMALL }}>
                                                            Gross Skins
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                            {(result.grossSkinPrizes && result.grossSkinPrizes.length > 0) ? (
                                                                <div style={{
                                                                    borderRadius: '8px',
                                                                    border: `1px solid ${theme.colors.BORDER}`,
                                                                    overflowX: 'auto'
                                                                }}>
                                                                    <table style={{
                                                                        borderCollapse: 'collapse',
                                                                        width: '100%',
                                                                        backgroundColor: theme.colors.BACKGROUND
                                                                    }}>
                                                                        <thead>
                                                                            <tr style={{
                                                                                backgroundColor: theme.colors.PRIMARY,
                                                                                color: theme.colors.WHITE
                                                                            }}>
                                                                                <th style={{
                                                                                    padding: '8px 8px',
                                                                                    textAlign: 'center',
                                                                                    fontWeight: '600',
                                                                                    fontSize: '0.9rem',
                                                                                }}>
                                                                                    Hole
                                                                                </th>
                                                                                <th style={{
                                                                                    padding: '8px 8px',
                                                                                    textAlign: 'left',
                                                                                    fontWeight: '600',
                                                                                    fontSize: '0.9rem',
                                                                                }}>
                                                                                    Player
                                                                                </th>
                                                                                <th style={{
                                                                                    padding: '8px 8px',
                                                                                    textAlign: 'center',
                                                                                    fontWeight: '600',
                                                                                    fontSize: '0.9rem',
                                                                                }}>
                                                                                    Gross
                                                                                </th>
                                                                                <th style={{
                                                                                    padding: '8px 8px',
                                                                                    textAlign: 'center',
                                                                                    fontWeight: '600',
                                                                                    fontSize: '0.9rem',
                                                                                }}>
                                                                                    Amount
                                                                                </th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {result.grossSkinPrizes && result.grossSkinPrizes.map((skin, index) => (
                                                                                <tr key={index} style={{
                                                                                    borderBottom: `1px solid ${theme.colors.BORDER}`
                                                                                }}>
                                                                                    <td style={{
                                                                                        padding: '8px 8px',
                                                                                        textAlign: 'center',
                                                                                        color: theme.colors.TEXT_SECONDARY,
                                                                                        fontSize: '0.9rem',
                                                                                    }}>
                                                                                        {skin.hole}
                                                                                    </td>
                                                                                    <td style={{
                                                                                        padding: '8px 8px',
                                                                                        color: theme.colors.TEXT,
                                                                                        fontWeight: '600',
                                                                                        fontSize: '1rem',
                                                                                    }}>
                                                                                        {getMemberName(skin.encodedEmail || '')}
                                                                                    </td>
                                                                                    <td style={{
                                                                                        padding: '8px 8px',
                                                                                        textAlign: 'center',
                                                                                        color: theme.colors.TEXT_SECONDARY,
                                                                                        fontSize: '0.9rem',
                                                                                    }}>
                                                                                        {skin.grossScore}
                                                                                    </td>
                                                                                    <td style={{
                                                                                        padding: '8px 8px',
                                                                                        textAlign: 'center',
                                                                                        color: theme.colors.TEXT_SECONDARY,
                                                                                        fontSize: '0.9rem',
                                                                                    }}>
                                                                                        ${skin.amount.toFixed(2)}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            ) : (
                                                                <div style={{
                                                                    color: theme.colors.TEXT_SECONDARY,
                                                                    fontStyle: 'italic',
                                                                    padding: theme.spacing.SMALL
                                                                }}>
                                                                    (N/A)
                                                                </div>
                                                            )}
                                                            <div style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                paddingTop: theme.spacing.MEDIUM,
                                                                marginTop: theme.spacing.MEDIUM,
                                                                borderTop: `2px solid ${theme.colors.PRIMARY}`
                                                            }}>
                                                                <div style={{ fontWeight: 700, color: theme.colors.PRIMARY, fontSize: '1.1em' }}>
                                                                    {result.grossSkinPrizes ? result.grossSkinPrizes.length : 0} of {Object.values(leagueModel.members).filter(member => member.grossSkins).length} Winners
                                                                </div>
                                                                <div style={{ fontWeight: 700, color: theme.colors.PRIMARY, fontSize: '1.1em' }}>
                                                                    ${(result.grossSkinPrizes ? result.grossSkinPrizes.reduce((total, skin) => total + (skin.amount || 0), 0) : 0).toFixed(2)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Net Skins Section */}
                                                    <div style={{ marginTop: theme.spacing.LARGE }}>
                                                        <div style={{ fontWeight: 600, color: theme.colors.TEXT, marginBottom: theme.spacing.SMALL }}>
                                                            Net Skins
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                            {((result.netSkinPrizes || []).length > 0) ? (
                                                                <div style={{
                                                                    borderRadius: '8px',
                                                                    border: `1px solid ${theme.colors.BORDER}`,
                                                                    overflowX: 'auto'
                                                                }}>
                                                                    <table style={{
                                                                        borderCollapse: 'collapse',
                                                                        width: '100%',
                                                                        backgroundColor: theme.colors.BACKGROUND
                                                                    }}>
                                                                        <thead>
                                                                            <tr style={{
                                                                                backgroundColor: theme.colors.SECONDARY,
                                                                                color: theme.colors.WHITE
                                                                            }}>
                                                                                <th style={{
                                                                                    padding: '8px 8px',
                                                                                    textAlign: 'center',
                                                                                    fontWeight: '600',
                                                                                    fontSize: '0.9rem',
                                                                                }}>
                                                                                    Hole
                                                                                </th>
                                                                                <th style={{
                                                                                    padding: '8px 8px',
                                                                                    textAlign: 'left',
                                                                                    fontWeight: '600',
                                                                                    fontSize: '0.9rem',
                                                                                }}>
                                                                                    Player
                                                                                </th>
                                                                                <th style={{
                                                                                    padding: '8px 8px',
                                                                                    textAlign: 'center',
                                                                                    fontWeight: '600',
                                                                                    fontSize: '0.9rem',
                                                                                }}>
                                                                                    Grs
                                                                                </th>
                                                                                <th style={{
                                                                                    padding: '8px 8px',
                                                                                    textAlign: 'center',
                                                                                    fontWeight: '600',
                                                                                    fontSize: '0.9rem',
                                                                                }}>
                                                                                    Net
                                                                                </th>
                                                                                <th style={{
                                                                                    padding: '8px 8px',
                                                                                    textAlign: 'center',
                                                                                    fontWeight: '600',
                                                                                    fontSize: '0.9rem',
                                                                                }}>
                                                                                    Amount
                                                                                </th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {(result.netSkinPrizes || []).map((skin, index) => (
                                                                                <tr key={index} style={{
                                                                                    borderBottom: `1px solid ${theme.colors.BORDER}`
                                                                                }}>
                                                                                    <td style={{
                                                                                        padding: '8px 8px',
                                                                                        textAlign: 'center',
                                                                                        color: theme.colors.TEXT_SECONDARY,
                                                                                        fontSize: '0.9rem',
                                                                                    }}>
                                                                                        {skin.hole}
                                                                                    </td>
                                                                                    <td style={{
                                                                                        padding: '8px 8px',
                                                                                        color: theme.colors.TEXT,
                                                                                        fontWeight: '600',
                                                                                        fontSize: '1rem',
                                                                                    }}>
                                                                                        {getMemberName(skin.encodedEmail || '')}
                                                                                    </td>
                                                                                    <td style={{
                                                                                        padding: '8px 8px',
                                                                                        textAlign: 'center',
                                                                                        color: theme.colors.TEXT_SECONDARY,
                                                                                        fontSize: '0.9rem',
                                                                                    }}>
                                                                                        {skin.grossScore}
                                                                                    </td>
                                                                                    <td style={{
                                                                                        padding: '8px 8px',
                                                                                        textAlign: 'center',
                                                                                        color: theme.colors.TEXT_SECONDARY,
                                                                                        fontSize: '0.9rem',
                                                                                    }}>
                                                                                        {skin.netScore}
                                                                                    </td>
                                                                                    <td style={{
                                                                                        padding: '8px 8px',
                                                                                        textAlign: 'center',
                                                                                        color: theme.colors.TEXT_SECONDARY,
                                                                                        fontSize: '0.9rem',
                                                                                    }}>
                                                                                        ${skin.amount.toFixed(2)}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            ) : (
                                                                <div style={{
                                                                    color: theme.colors.TEXT_SECONDARY,
                                                                    fontStyle: 'italic',
                                                                    padding: theme.spacing.SMALL
                                                                }}>
                                                                    (N/A)
                                                                </div>
                                                            )}
                                                            <div style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                paddingTop: theme.spacing.MEDIUM,
                                                                marginTop: theme.spacing.MEDIUM,
                                                                borderTop: `2px solid ${theme.colors.PRIMARY}`
                                                            }}>
                                                                <div style={{ fontWeight: 700, color: theme.colors.PRIMARY, fontSize: '1.1em' }}>
                                                                    {(result.netSkinPrizes || []).length} of {Object.values(leagueModel.members).filter(member => member.netSkins).length} Winners
                                                                </div>
                                                                <div style={{ fontWeight: 700, color: theme.colors.PRIMARY, fontSize: '1.1em' }}>
                                                                    ${(result.netSkinPrizes || []).reduce((total, skin) => total + (skin.amount || 0), 0).toFixed(2)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>


                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </Slider>
                    </div>
                </div>
            ) : (
                <div style={{ color: theme.colors.TEXT }}>No schedule items found.</div>
            )}
            {customPayoutModal.open && (
                <Modal
                    isOpen={customPayoutModal.open}
                    onClose={() => {
                        setCustomPayoutModal(prev => ({
                            ...prev,
                            open: false,
                            paying: false,
                        }));
                    }}
                    onConfirm={async () => {
                        setCustomPayoutModal(prev => ({ ...prev, paying: true }));
                        try {
                            const leagueAdminService = resolveType(LeagueAdminService);
                            await leagueAdminService.addCustomPayout(
                                leagueId || '',
                                customPayoutModal.scheduleDayId || '',
                                customPayoutModal.member,
                                parseFloat(customPayoutModal.amount),
                                customPayoutModal.description
                            );
                            setCustomPayoutModal({
                                open: false,
                                scheduleDayId: null,
                                member: '',
                                amount: '',
                                description: '',
                                paying: false,
                            });
                            await fetchData(customPayoutModal.scheduleDayId);
                        } finally {
                            setCustomPayoutModal(prev => ({ ...prev, paying: false }));
                        }
                    }}
                    title="Custom Payout"
                    content={
                        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.SMALL }}>
                            <div>
                                <label>Member:</label>
                                <Select
                                    value={leagueModel!.members[customPayoutModal.member] ? {
                                        value: customPayoutModal.member,
                                        label: `${leagueModel!.members[customPayoutModal.member].firstName} ${leagueModel!.members[customPayoutModal.member].lastName}`
                                    } : null}
                                    onChange={(option) => {
                                        setCustomPayoutModal(prev => ({
                                            ...prev,
                                            member: option?.value || '',
                                        }));
                                    }}
                                    options={Object.entries(leagueModel!.members)
                                        .filter(([_, member]) => member.isPlayer)
                                        .sort(([_, a], [__, b]) => {
                                            const firstNameCompare = a.firstName.localeCompare(b.firstName);
                                            return firstNameCompare !== 0 ? firstNameCompare : a.lastName.localeCompare(b.lastName);
                                        })
                                        .map(([encodedEmail, member]) => ({
                                            value: encodedEmail,
                                            label: `${member.firstName} ${member.lastName}`
                                        }))}
                                    styles={{
                                        control: (base: any) => ({
                                            ...base,
                                            backgroundColor: theme.colors.SURFACE,
                                            borderColor: theme.colors.BORDER,
                                            '&:hover': {
                                                borderColor: theme.colors.BORDER
                                            }
                                        }),
                                        menu: (base: any) => ({
                                            ...base,
                                            backgroundColor: theme.colors.SURFACE,
                                            border: `1px solid ${theme.colors.BORDER}`
                                        }),
                                        option: (base: any, state: any) => ({
                                            ...base,
                                            backgroundColor: state.isFocused ? theme.colors.ACCENT : theme.colors.SURFACE,
                                            color: state.isFocused ? theme.colors.WHITE : theme.colors.TEXT,
                                            '&:hover': {
                                                backgroundColor: theme.colors.ACCENT,
                                                color: theme.colors.WHITE
                                            }
                                        }),
                                        singleValue: (base: any) => ({
                                            ...base,
                                            color: theme.colors.TEXT,
                                        }),
                                    }}
                                />
                            </div>
                            <div>
                                <label>Amount ($):</label>
                                <input
                                    type="number"
                                    value={customPayoutModal.amount}
                                    onChange={e =>
                                        setCustomPayoutModal((prev) => ({
                                            ...prev,
                                            amount: e.target.value,
                                        }))
                                    }
                                    style={inputStyle}
                                />
                            </div>
                            <div>
                                <label>Description:</label>
                                <input
                                    type="text"
                                    value={customPayoutModal.description}
                                    onChange={e =>
                                        setCustomPayoutModal((prev) => ({
                                            ...prev,
                                            description: e.target.value,
                                        }))
                                    }
                                    style={inputStyle}
                                />
                            </div>
                        </div>
                    }
                    confirmText={'Pay'}
                    cancelText={'Cancel'}
                    disabled={
                        customPayoutModal.paying ||
                        !customPayoutModal.member ||
                        !customPayoutModal.amount ||
                        !customPayoutModal.description
                    }
                />
            )}
            {removePayoutModal.open && (
                <Modal
                    isOpen={removePayoutModal.open}
                    onClose={() => {
                        setRemovePayoutModal(prev => ({
                            ...prev,
                            open: false,
                        }));
                    }}
                    onConfirm={async () => {
                        try {
                            const leagueAdminService = resolveType(LeagueAdminService);
                            await leagueAdminService.removeCustomPayout(
                                leagueId || '',
                                removePayoutModal.scheduleDayId || '',
                                removePayoutModal.memberEmail,
                                removePayoutModal.payoutId
                            );
                            setRemovePayoutModal(prev => ({
                                ...prev,
                                open: false,
                            }));
                            await fetchData(removePayoutModal.scheduleDayId);
                        } catch (error) {
                            console.error('Error removing custom payout:', error);
                            setErrorModal({
                                open: true,
                                message: 'Failed to remove custom payout',
                            });
                        }
                    }}
                    title="Remove Custom Payout"
                    content={
                        <div style={{ color: theme.colors.TEXT }}>
                            Are you sure you want to remove this custom payout?
                            <div style={{
                                marginTop: theme.spacing.MEDIUM,
                                padding: theme.spacing.MEDIUM,
                                backgroundColor: theme.colors.SURFACE,
                                borderRadius: theme.borderRadius.SMALL,
                                border: `1px solid ${theme.colors.BORDER}`
                            }}>
                                <div style={{ fontWeight: 600 }}>{removePayoutModal.memberName}</div>
                                <div style={{ marginTop: theme.spacing.SMALL }}>
                                    <span style={{ fontWeight: 500 }}>${removePayoutModal.amount?.toFixed(2)}</span>
                                    {removePayoutModal.description && (
                                        <span style={{ marginLeft: theme.spacing.SMALL, color: theme.colors.TEXT_SECONDARY }}>
                                            - {removePayoutModal.description}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    }
                    confirmText={'Remove'}
                    cancelText={'Cancel'}
                    type="error"
                />
            )}
            {/* Pin Prize Assignment Modal */}
            {pinPrizeAssignmentModal.open && (
                <Modal
                    isOpen={pinPrizeAssignmentModal.open}
                    onClose={() => {
                        setPinPrizeAssignmentModal(prev => ({
                            ...prev,
                            open: false,
                        }));
                    }}
                    onConfirm={handleAssignPinPrize}
                    title="Assign Pin Prize"
                    content={
                        <div style={{ color: theme.colors.TEXT }}>
                            <div style={{ marginBottom: theme.spacing.MEDIUM }}>
                                <div style={{ fontWeight: 600 }}>Hole {pinPrizeAssignmentModal.hole} - {pinPrizeAssignmentModal.description}</div>
                                {pinPrizeAssignmentModal.teeName && (
                                    <div style={{ color: theme.colors.TEXT_SECONDARY, fontSize: '0.9em' }}>
                                        Tee: {pinPrizeAssignmentModal.teeName}
                                    </div>
                                )}
                                <div style={{ color: theme.colors.TEXT_SECONDARY }}>${pinPrizeAssignmentModal.amount.toFixed(2)}</div>
                            </div>
                            <div>
                                <label>Select Member:</label>
                                <Select
                                    value={leagueModel!.members[pinPrizeAssignmentModal.member] ? {
                                        value: pinPrizeAssignmentModal.member,
                                        label: `${leagueModel!.members[pinPrizeAssignmentModal.member].firstName} ${leagueModel!.members[pinPrizeAssignmentModal.member].lastName}`
                                    } : null}
                                    onChange={(option) => {
                                        setPinPrizeAssignmentModal(prev => ({
                                            ...prev,
                                            member: option?.value || '',
                                        }));
                                    }}
                                    options={Object.entries(leagueModel!.members)
                                        .filter(([_, member]) => member.isPlayer)
                                        .sort(([_, a], [__, b]) => {
                                            const firstNameCompare = a.firstName.localeCompare(b.firstName);
                                            return firstNameCompare !== 0 ? firstNameCompare : a.lastName.localeCompare(b.lastName);
                                        })
                                        .map(([encodedEmail, member]) => ({
                                            value: encodedEmail,
                                            label: `${member.firstName} ${member.lastName}`
                                        }))}
                                    styles={{
                                        control: (base: any) => ({
                                            ...base,
                                            backgroundColor: theme.colors.SURFACE,
                                            borderColor: theme.colors.BORDER,
                                            '&:hover': {
                                                borderColor: theme.colors.BORDER
                                            }
                                        }),
                                        menu: (base: any) => ({
                                            ...base,
                                            backgroundColor: theme.colors.SURFACE,
                                            border: `1px solid ${theme.colors.BORDER}`
                                        }),
                                        option: (base: any, state: any) => ({
                                            ...base,
                                            backgroundColor: state.isFocused ? theme.colors.ACCENT : theme.colors.SURFACE,
                                            color: state.isFocused ? theme.colors.WHITE : theme.colors.TEXT,
                                            '&:hover': {
                                                backgroundColor: theme.colors.ACCENT,
                                                color: theme.colors.WHITE
                                            }
                                        }),
                                        singleValue: (base: any) => ({
                                            ...base,
                                            color: theme.colors.TEXT,
                                        }),
                                    }}
                                />
                            </div>
                        </div>
                    }
                    confirmText="Assign"
                    cancelText="Cancel"
                    type="info"
                    confirmLoading={pinPrizeAssignmentModal.paying}
                    disabled={pinPrizeAssignmentModal.paying || !pinPrizeAssignmentModal.member}
                />
            )}
            {/* Unassign Pin Prize Modal */}
            {unassignPinPrizeModal.open && (
                <Modal
                    isOpen={unassignPinPrizeModal.open}
                    onClose={() => {
                        setUnassignPinPrizeModal(prev => ({
                            ...prev,
                            open: false,
                        }));
                    }}
                    onConfirm={async () => {
                        if (!leagueId || !unassignPinPrizeModal.scheduleDayId) return;
                        setUnassignPinPrizeModal(prev => ({ ...prev, paying: true }));
                        try {
                            const leagueAdminService = resolveType(LeagueAdminService);
                            await leagueAdminService.unassignPinPrize(
                                leagueId,
                                unassignPinPrizeModal.scheduleDayId,
                                unassignPinPrizeModal.prizeId
                            );
                            setUnassignPinPrizeModal(prev => ({ ...prev, open: false }));
                            await fetchData(unassignPinPrizeModal.scheduleDayId);
                        } catch (error) {
                            console.error('Error unassigning pin prize:', error);
                            setErrorModal({
                                open: true,
                                message: 'Failed to unassign pin prize',
                            });
                        } finally {
                            setUnassignPinPrizeModal(prev => ({ ...prev, paying: false }));
                        }
                    }}
                    title="Unassign Pin Prize"
                    content={
                        <div style={{ color: theme.colors.TEXT }}>
                            Are you sure you want to unassign this pin prize?
                            <div style={{
                                marginTop: theme.spacing.MEDIUM,
                                padding: theme.spacing.MEDIUM,
                                backgroundColor: theme.colors.SURFACE,
                                borderRadius: theme.borderRadius.SMALL,
                                border: `1px solid ${theme.colors.BORDER}`
                            }}>
                                <div style={{ fontWeight: 600 }}>{unassignPinPrizeModal.memberName}</div>
                                <div style={{ marginTop: theme.spacing.SMALL }}>
                                    <div style={{ fontWeight: 500 }}>Hole {unassignPinPrizeModal.hole} - {unassignPinPrizeModal.description}</div>
                                    {unassignPinPrizeModal.teeName && (
                                        <div style={{ color: theme.colors.TEXT_SECONDARY, fontSize: '0.9em' }}>
                                            Tee: {unassignPinPrizeModal.teeName}
                                        </div>
                                    )}
                                    <div style={{ color: theme.colors.TEXT_SECONDARY }}>${unassignPinPrizeModal.amount.toFixed(2)}</div>
                                </div>
                            </div>
                        </div>
                    }
                    confirmText={'Unassign'}
                    cancelText={'Cancel'}
                    type="error"
                    confirmLoading={unassignPinPrizeModal.paying}
                    disabled={unassignPinPrizeModal.paying}
                />
            )}
            {showCompleteConfirmModal && (
                <Modal
                    isOpen={showCompleteConfirmModal}
                    onClose={() => setShowCompleteConfirmModal(false)}
                    onConfirm={handleConfirmCompleteWeek}
                    title="Confirm Completion"
                    content={
                        <p>This is a one-way action; once completed a week cannot be re-calculated. Are you sure you want to continue?</p>
                    }
                    confirmText="Confirm"
                    cancelText="Cancel"
                    disabled={calculatingDayId !== null}
                    confirmLoading={calculatingDayId !== null}
                />
            )}
            {showLockConfirmModal && (
                <Modal
                    isOpen={showLockConfirmModal}
                    onClose={() => {
                        setShowLockConfirmModal(false);
                        setDayToLock(null);
                    }}
                    onConfirm={handleConfirmLockDay}
                    title="Confirm Lock Day"
                    content={
                        <div style={{ color: theme.colors.TEXT }}>
                            <p style={{ marginBottom: theme.spacing.MEDIUM }}>
                                <strong>Warning:</strong> This is a one-way action. Once a day is locked:
                            </p>
                            <ul style={{
                                margin: 0,
                                paddingLeft: theme.spacing.LARGE,
                                color: theme.colors.TEXT_SECONDARY
                            }}>
                                <li>No new rounds can be submitted for this day</li>
                                <li>Existing rounds cannot be modified</li>
                                <li>Pin prizes can still be assigned</li>
                                <li>This action cannot be undone</li>
                            </ul>
                            <p style={{
                                marginTop: theme.spacing.MEDIUM,
                                fontWeight: 600,
                                color: theme.colors.ERROR
                            }}>
                                Are you sure you want to lock this day?
                            </p>
                        </div>
                    }
                    confirmText="Lock Day"
                    cancelText="Cancel"
                    type="warning"
                />
            )}
            {showCancelConfirmModal && (
                <Modal
                    isOpen={showCancelConfirmModal}
                    onClose={() => {
                        setShowCancelConfirmModal(false);
                        setDayToCancel(null);
                    }}
                    onConfirm={handleConfirmCancelDay}
                    title="Confirm Cancel Day"
                    content={
                        <div style={{ color: theme.colors.TEXT }}>
                            <p style={{ marginBottom: theme.spacing.MEDIUM }}>
                                <strong>Warning:</strong> Cancelling a round is a one-way operation.
                            </p>
                            <ul style={{
                                margin: 0,
                                paddingLeft: theme.spacing.LARGE,
                                color: theme.colors.TEXT_SECONDARY
                            }}>
                                <li>The day will be marked as cancelled</li>
                                <li>No rounds can be submitted for this day</li>
                                <li>This action cannot be undone</li>
                            </ul>
                            <p style={{
                                marginTop: theme.spacing.MEDIUM,
                                fontWeight: 600,
                                color: theme.colors.ERROR
                            }}>
                                Do you wish to continue?
                            </p>
                        </div>
                    }
                    confirmText="Cancel Day"
                    cancelText="No, Keep Day"
                    type="error"
                />
            )}
            {errorModal.open && (
                <Modal
                    isOpen={errorModal.open}
                    onClose={() => {
                        setErrorModal({
                            open: false,
                            message: '',
                        });
                    }}
                    title="Error"
                    content={
                        <div style={{ color: theme.colors.TEXT }}>
                            {errorModal.message}
                        </div>
                    }
                    confirmText="OK"
                    type="error"
                />
            )}
        </div>
    );
};

export default LeagueAdmin; 